import { IAlertProps } from "@components/alert/Alert.types";
import BusyIndicator from "@components/busyIndicator";
import { WithBusyIndicator, withBusyIndicator } from "@components/busyIndicator/withBusyIndicator";
import { Button, ButtonGroup } from "@components/button";
import Dialog from "@components/dialog";
import { LastFieldWrapper } from "@components/fastEntry/FastEntry.styles";
import Header from "@components/header/Header";
import { CaretIcon } from "@components/icon";
import Field from "@components/inputs/field";
import FieldsWrapper from "@components/inputs/field/FieldsWrapper";
import { BasicSelect } from "@components/inputs/select";
import { ISelectionChangeArgs } from "@components/inputs/select/Select.types";
import Summary from "@components/summary";
import SummaryItem, { ISummaryItemProps } from "@components/summary/SummaryItem";
import Wizard, { IStepChangeArgs } from "@components/wizard/Wizard";
import { withTheme } from "@hoc/withTheme";
import { createBindingContext, IEntity, TEntityKey } from "@odata/BindingContext";
import { getExpandedEnumProps } from "@odata/Data.utils";
import {
    EntityTypeName,
    IPrEmployeeEntity,
    IPrSubmissionEntity,
    IPrSubmissionSubtypeEntity,
    PrSubmissionEntity,
    PrSubmissionRegZecEntity
} from "@odata/GeneratedEntityTypes";
import { PrSubmissionSubtypeCode, PrSubmissionTypeCode } from "@odata/GeneratedEnums";
import { getEnumNameSpaceName } from "@odata/GeneratedEnums.utils";
import { isSuccessResponseHandler } from "@odata/OData";
import { prepareQuery } from "@odata/OData.utils";
import { WithOData, withOData } from "@odata/withOData";
import { IDefinition, IGetDefinition } from "@pages/PageUtils";
import {
    getEntitySetFromType,
    getOdataFuncEntitySetForSubmissionType,
    getOdataFunctionNameForSubmissionType,
    getSubmissionDefinitionByTypes,
    getSubmissionSubtypes,
    getSubtypeSelectItems
} from "@pages/payroll/submission/Submission.utils";
import { ViewStyled } from "@pages/payroll/submission/submissionWizard/SubmissionWizard.styles";
import { isObjectEmpty } from "@utils/general";
import { logger } from "@utils/log";
import memoize from "@utils/memoize";
import { memoizeOne } from "@utils/memoizeOne";
import i18next from "i18next";
import { cloneDeep } from "lodash";
import React, { ReactElement } from "react";
import { WithTranslation, withTranslation } from "react-i18next";

import { NEW_ITEM_DETAIL } from "../../../../constants";
import { AppContext, IAppContext } from "../../../../contexts/appContext/AppContext.types";
import { BasicInputSizes, IconSize, PageViewMode, ValidationErrorType } from "../../../../enums";
import { TRecordString } from "../../../../global.types";
import { IValidationError } from "../../../../model/Validator.types";
import TestIds from "../../../../testIds";
import { PropsWithTheme } from "../../../../theme";
import { FormType, getAlertFromError } from "../../../../views/formView/Form.utils";
import { FormStorage, IFormAlert } from "../../../../views/formView/FormStorage";
import { FormViewForExtend, IFormViewProps } from "../../../../views/formView/FormView";
import { FormContent, FormFooter } from "../../../../views/formView/FormView.styles";
import ConfirmationButtons from "../../../../views/table/ConfirmationButtons";
import { VIEW_PADDING_VALUE } from "../../../../views/View.styles";


interface IProps {
    onCancel: () => void;
    onConfirm: (createdEntity?: IEntity) => void;
    mainType: PrSubmissionTypeCode;
    subType?: PrSubmissionSubtypeCode;
    defaultData?: IEntity;
    employee?: IPrEmployeeEntity;
    generatorKey?: TEntityKey;
    summaryItems?: ISummaryItemProps[];
}

interface IState {
    storage?: FormStorage;
    allSubtypes?: Map<PrSubmissionSubtypeCode, IPrSubmissionSubtypeEntity>;
    subType?: PrSubmissionSubtypeCode;
    subTypeTemp?: PrSubmissionSubtypeCode;
    showValidation?: boolean;
}

enum SubmissionSteps {
    TypeSelection = 0,
    Form = 1
}

type TFullProps = IProps & WithBusyIndicator & PropsWithTheme & WithOData & WithTranslation;

const SUBMISSION_FORM_ID = "SubmissionWizardForm";

class SubmissionWizard extends React.Component<TFullProps, IState> {
    static contextType = AppContext;

    _formRef = React.createRef<FormViewForExtend<IFormViewProps<IPrSubmissionEntity>>>();

    getDef: IGetDefinition;
    def: IDefinition;

    constructor(props: TFullProps, context: IAppContext) {
        super(props);

        const storage = this.createStorage(context);

        const initialState: Partial<IState> = {};

        if (!this.isWizard) {
            // "picked" state
            initialState.subType = props.subType;
            // in case there is no subtype in the props -> error or pick default?
        } else if (this.props.subType) {
            // preselected value
            initialState.subTypeTemp = props.subType;
        }

        this.state = {
            storage,
            ...initialState
        };
    }

    componentDidMount() {
        if (!this.isWizard) {
            this.init();
        } else {
            this.loadSubtypes();
        }
    }

    get isSaved(): boolean {
        return this.state.storage.pageViewMode === PageViewMode.FormReadOnly;
    }

    get subType(): PrSubmissionSubtypeCode {
        return this.state.subType;
    }

    createStorage(context: IAppContext): FormStorage {
        return new FormStorage({
            id: SUBMISSION_FORM_ID,
            oData: this.props.oData,
            t: this.props.t,
            theme: this.props.theme,
            context,
            refresh: () => {
                this.forceUpdate();
                this._formRef.current?.forceUpdate();
            }
        });
    }

    /**
     * Subtypes for wizard - if there are more subtypes defined for the main type,
     * wizard is rendered and user can select subtype in the first step.
     * Otherwise, the form is rendered directly - subtype is predefined in the props (NEMPRI, HZUPN) or empty (HOZ).
     */
    subTypes = memoizeOne((): PrSubmissionSubtypeCode[] => {
        switch (this.props.mainType) {
            case PrSubmissionTypeCode.EmployerDiscountOnSocialInsurance:
                return [
                    PrSubmissionSubtypeCode.OzuspojRegistration,
                    PrSubmissionSubtypeCode.OzuspojTermination,
                    PrSubmissionSubtypeCode.OzuspojCancellation,
                ];
            case PrSubmissionTypeCode.EmployeeRegistration:
                return [
                    PrSubmissionSubtypeCode.RegZecStart,
                    PrSubmissionSubtypeCode.RegZecTermination,
                    PrSubmissionSubtypeCode.RegZecChange,
                    PrSubmissionSubtypeCode.RegZecCorrection,
                    PrSubmissionSubtypeCode.RegZecCzechOriginStart,
                    PrSubmissionSubtypeCode.RegZecCzechOriginEnd,
                    PrSubmissionSubtypeCode.RegZecCancellation
                ];
            default:
                return [];
        }
    }, () => [this.props.mainType]);

    get isWizard(): boolean {
        return this.subTypes()?.length > 0;
    }

    getDefaultData = async (): Promise<IEntity> => {
        const { oData } = this.state.storage;
        const metadata = oData.getMetadata();
        const entitySet = getEntitySetFromType(metadata, this.props.mainType, this.subType);

        const bindingContext = createBindingContext(entitySet, this.props.oData.getMetadata()).addKey(NEW_ITEM_DETAIL, true);
        this.state.storage.storeSingleValue("definition", this.def.form);
        this.state.storage.storeSingleValue("bindingContext", bindingContext);
        const mergedDef = this.state.storage.collectColumnData(bindingContext, this.state.storage.getVariant());

        const query = prepareQuery({
            bindingContext: createBindingContext(entitySet, metadata),
            fieldDefs: this.state.storage.convertMergedDefToColumns(mergedDef),
            oData,
        });

        const funcName = getOdataFunctionNameForSubmissionType(this.props.mainType, this.subType);
        const entitySetNameForFn = getOdataFuncEntitySetForSubmissionType(this.props.mainType, this.subType);

        let result: IEntity = {};

        if (funcName && entitySetNameForFn) {
            const parameters: TRecordString = {};

            if (this.props.mainType === PrSubmissionTypeCode.EmployeeRegistration) {
                parameters.TypeCode = this.subType;
            }

            try {
                const response = await oData.getEntitySetWrapper(entitySetNameForFn)
                    .func(funcName, this.props.generatorKey, {
                        parameters,
                        queryParameters: query.getParams()
                    });

                if (isSuccessResponseHandler(response)) {
                    result = (Array.isArray(response.value) ? response.value[0] : response.value ?? response) as IEntity;

                    // Enhance enum values with display values
                    await query._enhanceValueWithEnumExpands(result);
                } else {
                    // handle error...
                    logger.error("Unknow response while getting default data for submission wizard");
                }
            } catch (e) {
                logger.error("Error while getting default data for submission wizard", e);
            }
        }

        if (this.subType) {
            // propagate selected subtype to the result, if set in the first step of the wizard
            result = {
                ...result,
                ...getExpandedEnumProps(EntityTypeName.PrSubmissionSubtype, this.subType, PrSubmissionEntity.SubmissionSubtype)
            };
        }

        // initiate missing keys from props.defaultData
        const { defaultData } = this.props;
        if (defaultData) {
            for (const key of Object.keys(defaultData)) {
                if (isObjectEmpty(result[key])) {
                    result[key] = defaultData[key];
                } else if (([PrSubmissionRegZecEntity.Employment, PrSubmissionRegZecEntity.Employee] as string[]).includes(key)) {
                    // for Employee and Employment, add default data
                    if (isObjectEmpty(result[key][key])) {
                        result[key][key] = defaultData[key][key];
                    }
                }
            }
        }

        const employee = result?.Detail?.Employee?.Employee ?? result?.Employee?.Employee;
        if (employee) { // todo: handle for each type of submission
            result.EmployeeHeader = {
                Id: employee.Id,
                LastName: employee.LastName,
                FirstName: employee.FirstName,
            };
        }

        return result;
    };

    async loadSubtypes(): Promise<void> {
        const subtypes = await getSubmissionSubtypes(this.props.oData);
        const allSubtypes = new Map<PrSubmissionSubtypeCode, IPrSubmissionSubtypeEntity>();
        subtypes.forEach(subtype => {
            allSubtypes.set(subtype.Code, subtype);
        });
        this.setState({ allSubtypes });
    }

    init = async () => {
        this.getDef = getSubmissionDefinitionByTypes(this.props.mainType, this.subType);
        if (!this.getDef) {
            logger.error("Definition is not found for provided type combination");
            return;
        }
        await i18next.loadNamespaces(this.getDef.translationFiles);

        this.def = this.getDef(this.context, {
            isSingleEmployeeView: true,
            type: this.props.mainType,
            subType: this.subType
        });

        const metadata = this.props.oData.getMetadata();
        const entitySet = getEntitySetFromType(metadata, this.props.mainType, this.subType);
        const bindingContext = createBindingContext(entitySet, metadata).addKey(NEW_ITEM_DETAIL, true);

        const defaultData = await this.getDefaultData();

        await this.state.storage.init({
            definition: cloneDeep(this.def.form),
            bindingContext,
            ignoreVariants: true,
            data: defaultData,
            onAfterLoad: () => this.forceUpdate()
        });
    };

    getSteps = memoize((): string[] => {
        const steps: string[] = [];
        if (!this.isWizard) {
            return [];
        }

        const list = [SubmissionSteps.TypeSelection, SubmissionSteps.Form];
        list.forEach((step, idx) => {
            const key = `Submission:Wizard.Step_${this.props.mainType}`;
            const title = step === SubmissionSteps.TypeSelection && i18next.exists(key) ? i18next.t(key) : i18next.t(`Submission:Wizard.Title`);
            steps.push(title.toString());
        });

        return steps;
    }, () => `${this.props.mainType}_${this.props.subType}_${this.props.tReady}`);

    handleChangeStep = (nextStep: number, currentStep: number): void => {
        const subTypeHasChanged = this.state.subTypeTemp !== this.state.subType;
        if (currentStep === 0 && (!this.state.subType || subTypeHasChanged)) {
            const storage = subTypeHasChanged ? this.createStorage(this.context) : this.state.storage;
            this.setState({
                storage,
                subType: this.state.subTypeTemp
            }, () => { this.init(); });
            return;
        }
    };

    /**
     * Confirm of change price editable window action, todo: ...
     */
    handleWizardConfirm = async (): Promise<void> => {
        let alert: IAlertProps;
        this.state.storage.setFormAlert(null);

        try {
            // newly created items doesn't load after save, they wait to the route change. Consider to force
            // the load as the route won't change in the editable window.
            await this._formRef.current?.save();
        } catch (e) {
            alert = getAlertFromError(e);
            this.state.storage.setFormAlert(alert as unknown as IFormAlert);
        }
        this.state.storage.setBusy(false);
    };

    handleCloseWithConfirm = (): void => {
        if (this.isSaved) {
            const createdEntity = this.state.storage.data.entity;
            createdEntity.Id = this.state.storage.data.bindingContext.getKey();
            this.props.onConfirm(createdEntity);
        } else {
            this.props.onCancel();
        }
    }

    handleAfterSave = (): void => {
        const { storage } = this.state;
        storage.pageViewMode = PageViewMode.FormReadOnly;
        // success -> close the wizard
        this.handleCloseWithConfirm();
    };

    checkStepValidity = async (args: IStepChangeArgs): Promise<boolean> => {
        const { nextStep, isComplete } = args;
        const isValid = !!this.props.subType || !!this.state.subTypeTemp;
        if (!isValid) {
            this.setState({ showValidation: true });
        }
        return isValid;
    };

    renderForm(title: string, buttons: React.ReactElement): React.ReactElement {
        if (!this.def?.form) {
            return <BusyIndicator/>;
        }
        const FormControl = this.def.form.formControl;
        const { storage } = this.state;

        const formButtons = this.isSaved ? (
            <Button hotspotId={"wizardCloseButton"}
                    onClick={this.handleCloseWithConfirm}
                    isTransparent>{storage.t("Common:General.Close")}</Button>
        ) : buttons;

        const isVirtualized = this.def?.form?.type === FormType.VirtualizedList;

        const style: Partial<CSSStyleDeclaration> = isVirtualized ? {} : {
            padding: `${VIEW_PADDING_VALUE}px ${VIEW_PADDING_VALUE}px 0`
        };

        return storage && (
            <FormControl title={title}
                         buttons={formButtons}
                         ref={this._formRef}
                         storage={storage}
                         onAfterSave={this.handleAfterSave}
                         formProps={{
                             hideBreadcrumbs: true,
                             shouldHideVariant: true,
                             shouldHideAuditTrail: true,
                             withoutPadding: true,
                             buttonsOnBottom: true,
                             style
                         }}/>
        );
    }

    handleSubTypeChange = (e: ISelectionChangeArgs): void => {
        if (e.triggerAdditionalTasks) {
            this.setState({ subTypeTemp: e.value as PrSubmissionSubtypeCode });
        }
    };

    handleFirstStepSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
        this.handleChangeStep(SubmissionSteps.Form, SubmissionSteps.TypeSelection);
    };

    renderTypeSelection(title: string, buttons: ReactElement): React.ReactElement {
        const items = getSubtypeSelectItems(this.props.mainType, this.state.allSubtypes, this.subTypes());
        const displayAdditionalColumns = !!items[0]?.tabularData;

        const { summaryItems } = this.props;
        const afterIconsContent = summaryItems?.length ? (
            <Summary>
                {summaryItems.map(summaryItem => (
                    <SummaryItem key={summaryItem.id?.toString()} {...summaryItem} />
                ))}
            </Summary>
        ) : null;

        const showValidation = this.state.showValidation;
        const validationError: IValidationError = showValidation && !this.state.subTypeTemp ? {
            errorType: ValidationErrorType.Field,
            message: this.props.t("Common:Validation.Required")
        } : undefined;

        return (
            <ViewStyled hotspotContextId={"submissionTypeSelection"}>
                <FormContent data-testid={TestIds.FormContent}
                             onSubmit={this.handleFirstStepSubmit}
                             buttonsOnBottom={true}>
                    <Header title={title} afterIconsContent={afterIconsContent}/>
                    <FieldsWrapper style={{ width: "100%", maxWidth: "100%" }}>
                        <LastFieldWrapper>
                            <Field label={i18next.t("Submission:Wizard.SubType")}>
                                <BasicSelect
                                    inputIcon={<CaretIcon width={IconSize.M}/>}
                                    value={this.state.subTypeTemp}
                                    items={items}
                                    error={validationError}
                                    columns={displayAdditionalColumns ? [{ id: "MpsvCode" }, { id: "Name" }] : undefined}
                                    shouldDisplayAdditionalColumns={displayAdditionalColumns}
                                    onChange={this.handleSubTypeChange}
                                    width={BasicInputSizes.L}
                                    closeOnSelection
                                    openOnClick
                                />
                            </Field>
                        </LastFieldWrapper>
                    </FieldsWrapper>
                </FormContent>
                <FormFooter
                    hasTopMargin={true}
                    data-testid={TestIds.Footer}>
                    <ButtonGroup wrap={"wrap"}>
                        {buttons}
                    </ButtonGroup>
                </FormFooter>
            </ViewStyled>
        );
    }

    render() {
        return (
            <Dialog isEditableWindow
                    onConfirm={this.handleWizardConfirm}
                    onClose={this.isSaved ? this.handleCloseWithConfirm : this.props.onCancel}>
                {this.isWizard ? (
                    <Wizard steps={this.getSteps()}
                            step={this.subType ? SubmissionSteps.Form : SubmissionSteps.TypeSelection}
                            canChangeStep={this.checkStepValidity}
                            onCancel={this.props.onCancel}
                            onConfirm={this.handleWizardConfirm}
                            onChangeStep={this.handleChangeStep}
                            confirmText={i18next.t("Submission:Wizard.ConfirmText")}
                            isConfirmDisabled={false}>
                        {(currentStep, title, buttons) => {
                            switch (currentStep) {
                                case SubmissionSteps.Form:
                                    return this.renderForm(title, buttons);
                                case SubmissionSteps.TypeSelection:
                                    return this.renderTypeSelection(title, buttons);
                                default:
                                    return null;
                            }
                        }}
                    </Wizard>
                ) : this.renderForm(i18next.t("Submission:Wizard.Title"), (
                    <ConfirmationButtons
                        onConfirm={this.handleWizardConfirm}
                        onCancel={this.props.onCancel}
                        confirmText={i18next.t("Submission:Wizard.ConfirmText")}/>
                ))}
            </Dialog>
        );
    }
}

export default withTranslation([
    "Submission", "Common",
    getEnumNameSpaceName(EntityTypeName.PrSubmissionType),
    getEnumNameSpaceName(EntityTypeName.PrSubmissionSubtype)
])(withTheme(withBusyIndicator()(withOData(SubmissionWizard))));